// src/apiClient.ts
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../msalConfig';
import { toast } from 'react-hot-toast';
import { logger } from './logger';

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public details?: any
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

interface ApiClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
}

export class ApiClient {
  private static instance: ApiClient;
  private config: ApiClientConfig;

  private constructor(
    private msalInstance: IPublicClientApplication,
    private account: AccountInfo | null,
    config?: Partial<ApiClientConfig>
  ) {
    this.config = {
      baseUrl: config?.baseUrl || process.env.REACT_APP_API_BASE_URL || 'https://localhost:7249',
      defaultHeaders: {
        'Content-Type': 'application/json',
        ...config?.defaultHeaders,
      },
    };
  }

  public static getInstance(
    msalInstance: IPublicClientApplication,
    account: AccountInfo | null,
    config?: Partial<ApiClientConfig>
  ): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(msalInstance, account, config);
    }
    return ApiClient.instance;
  }

  public async getAccessToken(): Promise<string> {
    try {
      const tokenResp = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: this.account!,
      });
      return tokenResp.accessToken;
    } catch (silentError) {
      logger.warn('Silent token acquisition failed, trying popup', silentError);
      try {
        const popupResp = await this.msalInstance.acquireTokenPopup(loginRequest);
        return popupResp.accessToken;
      } catch (popupError) {
        logger.error('Popup token acquisition failed', popupError);

        await this.msalInstance.logoutPopup();

        throw new Error('Unable to acquire access token');
      }
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const accessToken = await this.getAccessToken();
      const url = `${this.config.baseUrl}/${endpoint}`;
      const defaultHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      });
      if (!response.ok) {
        const raw = await response.text();         
        let errorDetails: any = raw;
        try {
          errorDetails = JSON.parse(raw);          
        } catch (_) {
        }
        throw new ApiError(response.status, response.statusText, errorDetails);
      }
      const contentType = response.headers.get('Content-Type') ?? '';
      if (contentType.includes('application/json')) {
        return (await response.json()) as T;
      }
      const txt = await response.text();
      return txt as unknown as T;
    } catch (error) {
      logger.error('API request failed', error);
      throw error;
    }
  }

  public async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  public async post<T>(endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    const req = this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
    return withToast('POST', req);
  }

  public async put<T>(endpoint: string, body?: any, options?: RequestInit): Promise<T> {
    const req = this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body,
    });
    return withToast('PUT', req);
  }

  public async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const req = this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
    return withToast('DELETE', req);
  }
}

function extractMessage(data: any): string | undefined {
  if (!data) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data) return String(data.message);
  return undefined;
}

function extractErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    const d = err.details;
    if (typeof d === 'string' && d.trim() !== '') return d;

    if (d && typeof d === 'object' && 'message' in d) {
      const msg = String((d as any).message).trim();
      if (msg) return msg;
    }
    return undefined;
  }
  return undefined;
}
function withToast<T>(verb: 'POST' | 'PUT' | 'DELETE', promise: Promise<T>): Promise<T> {
  const verbMap = {
    POST: { loading: 'Saving…', fallbackSuccess: 'The action was performed successfully.', fallbackError: 'Something wrong happened.' },
    PUT: { loading: 'Updating…', fallbackSuccess: 'The action was performed successfully.', fallbackError: 'Something wrong happened.' },
    DELETE: { loading: 'Deleting…', fallbackSuccess: 'Deleted!', fallbackError: 'Something wrong happened.' },
  } as const;

  return toast.promise(
    promise,
    {
      loading: verbMap[verb].loading,
      success: (data) => extractMessage(data) ?? verbMap[verb].fallbackSuccess,
      error: (err) => extractErrorMessage(err) ?? verbMap[verb].fallbackError,
    },
    { id: `${verb}:${Date.now()}` }
  );
}
